import { useState, useCallback } from "react";
import { usePublishResponse, useMarkResponded } from "./use-reviews";

interface UseQuickPublishOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface UseQuickPublishReturn {
  publish: (reviewId: string, responseText: string) => void;
  isPublishing: boolean;
  publishSuccess: boolean;
  error: string | null;
  resetError: () => void;
}

export function useQuickPublish(options: UseQuickPublishOptions = {}): UseQuickPublishReturn {
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const publishMutation = usePublishResponse();
  const markRespondedMutation = useMarkResponded();

  const publish = useCallback(
    (reviewId: string, responseText: string) => {
      if (!responseText || isPublishing) return;

      setIsPublishing(true);
      setError(null);

      publishMutation.mutate(
        { id: reviewId, responseText },
        {
          onSuccess: async (data) => {
            if (data.publishType === "ASSISTED" && data.deepLinkUrl) {
              await navigator.clipboard.writeText(responseText).catch((err) => {
                console.error("Failed to copy to clipboard:", err);
              });

              window.open(data.deepLinkUrl, "_blank", "noopener,noreferrer");

              markRespondedMutation.mutate(
                { id: reviewId, responseText },
                {
                  onError: (err) => {
                    console.error("Failed to mark review as responded:", err);
                  },
                }
              );
            }
            setPublishSuccess(true);
            setIsPublishing(false);
            options.onSuccess?.();
          },
          onError: (err) => {
            console.error("Failed to publish response:", err);
            setError("Failed to publish response. Please try again.");
            setIsPublishing(false);
            options.onError?.(err as Error);
          },
        }
      );
    },
    [isPublishing, publishMutation, markRespondedMutation, options]
  );

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  return { publish, isPublishing, publishSuccess, error, resetError };
}
